/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react-hooks/exhaustive-deps */
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { subDays } from "date-fns";
import { Select } from "@mantine/core";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useAccountPageParams from "../../../../hooks/useAccountPageParams";
import uuid from "react-uuid";
import useLocalStorageState from "use-local-storage-state";
import { getCompanyEventActivity, getCompanyEventList } from "../../../../services/companyEndpoint";
import { LegacyColumn } from "../../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../../shared/table/TanStackTable";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import dateConverter from "../../../../utils/dateConverter";
import LeftEventsBar from "./components/LeftEventsBar";

const AccountEvents = () => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const metricList = useMemo(() => [
    { id: 1, name: "Last 24 hours" },
    { id: 2, name: "Last 7 days" },
    { id: 3, name: "Last 30 days" },
    { id: 4, name: "Last 90 days", default: true },
    { id: 5, name: "Last 180 days" },
    { id: 6, name: "Last 365 days" },
    { id: 7, name: "Custom range" },
  ], []);

  const tableHeight = useMemo(
    () => window.innerHeight - 365,
    [],
  );

  const [tableColumns] = useLocalStorageState<LegacyColumn[]>("he-events-prefs", {
    defaultValue: [
      {
        key: "users.name",
        dataType: "string",
        title: "User",
        colGroup: { style: { width: 220 } },
        isSortable: true,
      },
      {
        key: "timestamp",
        dataType: "date",
        title: "Timestamp",
        colGroup: { style: { width: 200 } },
        isSortable: true,
      },
      {
        key: "traits",
        dataType: "object",
        title: "Event Properties",
        colGroup: { style: { width: 320 } },
        isSortable: false,
      },
    ],
  });

  const params: any = useAccountPageParams();
  const [rows, setRows] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [metric, setMetric] = useState<any>(metricList[3]);
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [activityLoading, setActivityLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [pageIndex, setPageIndex] = useState<number>(0);
  const pageSize = 20;
  const [total, setTotal] = useState<number>(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total],
  );

  const calcDate: any = useMemo(() => {
    if (!metric?.name) {
      return new Date("1-1-1970");
    }

    if (metric.name === "Last 24 hours") {
      const now = new Date();
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    }

    const val = parseInt(metric.name.match(/[0-9]+/) ? metric.name.match(/[0-9]+/)[0] : "1", 10);
    return subDays(new Date(), val);
  }, [metric?.name]);

  useEffect(() => {
    let cancelled = false;

    const loadEventNames = async () => {
      setEventsLoading(true);
      try {
        const data = await getCompanyEventList({
          workspace_identifier: workspaceId,
          search_query: query,
          external_id: params.accountId,
          from_date: dateConverter(calcDate),
        });

        if (cancelled) return;

        const nextEvents = data?.data?.results || [];
        setEvents(nextEvents);

        if (!nextEvents.length) {
          setRows([]);
          setTotal(0);
          setSelectedEvent("");
          return;
        }

        setSelectedEvent((current) => (
          current && nextEvents.some((event: any) => event?.event_name === current)
            ? current
            : nextEvents[0]?.event_name
        ));
      } catch (error) {
        if (cancelled) return;
        setEvents([]);
        setRows([]);
        setTotal(0);
        setSelectedEvent("");
      } finally {
        if (!cancelled) {
          setEventsLoading(false);
        }
      }
    };

    loadEventNames();

    return () => {
      cancelled = true;
    };
  }, [query, metric?.name, calcDate, workspaceId, params.accountId]);

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    setSorting((nextSorting || []).slice(0, 1));
  }, [sorting]);

  const handleSelectedEvent = useCallback((eventName: string) => {
    setPageIndex(0);
    setSelectedEvent(eventName);
  }, []);

  const fetchData = useCallback(async (page: number) => {
    if (!selectedEvent) {
      setRows([]);
      setTotal(0);
      return;
    }

    setActivityLoading(true);
    try {
      const response = await getCompanyEventActivity({
        workspace_identifier: workspaceId,
        event_name: selectedEvent,
        external_id: params.accountId,
        from_date: dateConverter(calcDate),
        offset: page * pageSize,
        limit: pageSize,
      });
      if (response?.data) {
        const temp = response?.data?.results.map((item: any) => {
          const value = { ...item };
          if (value?.name || value?.email) {
            value["users.name"] = {
              name: value?.name,
              desc: value?.email,
              img: value?.avatar_url,
              externalId: value?.user_id,
              relation: "user",
              accountId: params.accountId,
            };
            delete (value.name);
            delete (value.email);
            delete (value?.avatar_url);
            delete (value?.user_id);
          }
          value.randomId = uuid();
          return value;
        });
        const totalHits = response.data.total_hits || 0;
        setRows(temp);
        setTotal(totalHits);
        if (totalHits <= (page * pageSize) && page > 0) {
          setPageIndex(0);
        }
      } else {
        setRows([]);
        setTotal(0);
        setPageIndex(0);
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
      setPageIndex(0);
    } finally {
      setActivityLoading(false);
    }
  }, [
    selectedEvent,
    workspaceId,
    params.accountId,
    calcDate,
    pageSize,
  ]);

  useEffect(() => {
    fetchData(pageIndex);
  }, [pageIndex, fetchData]);

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden">
      <LeftEventsBar
        selected={selectedEvent}
        setSelected={handleSelectedEvent}
        events={events}
        query={query}
        onChange={(e: any) => { setQuery(e.target.value); }}
        loading={eventsLoading}
      />
      <div className="flex-1 bg-white">
        <div className="flex justify-between items-center py-3 px-4 border-b border-gray-200">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedEvent ? selectedEvent.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "Select an event"}
              {total > 0 && (
                <span className="ml-3 text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                  {total} events
                </span>
              )}
            </h2>
          </div>
          <Select
            value={String(metric?.id ?? "")}
            onChange={(value) => {
              const item = metricList.find((m) => String(m.id) === value);
              if (item) {
                setMetric(item);
                setRows([]);
                setTotal(0);
                setPageIndex(0);
                setSorting([]);
                setSelectedEvent("");
              }
            }}
            data={metricList.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            size="sm"
            classNames={{
              input: "text-sm",
              root: "w-[180px]",
            }}
          />
        </div>

        <div>
          {selectedEvent ? (
            <TanStackTable
              legacyColumns={tableColumns || []}
              data={rows}
              totalData={total}
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageCount={pageCount}
              onPageIndexChange={setPageIndex}
              sorting={sorting}
              onSortingChange={onTableSortingChange}
              loading={activityLoading}
              itemType="Events"
              noDataTitle="No Event Activity"
              noDataMessage="No activity found for this event in the selected range."
              height={tableHeight}
              surfaceVariant="lovable"
              enableColumnPinning={false}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-medium text-gray-700 mb-2">Select an event to view details</h3>
              <p className="text-gray-500 max-w-md">Choose an event from the list on the left to see its activity feed and properties</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountEvents;
